// Turning a won lead into a client (WP-C1).
//
// Deliberately a FORM, not a side effect of dragging a card into Спечелена. A stage change says
// where a deal stands; creating a client record raises a real charge with a real price on it, and
// that should never happen because a drag landed one column too far.
//
// The lead list here is restricted to leads in a terminal "won" stage, so the dialog cannot
// quietly convert someone who has not agreed to anything.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isDemoMode, supabase } from '../../lib/supabase.js';
import { useConvertLead } from './useClients.js';

interface WonLead {
  id: number;
  business_name: string;
  website_url: string | null;
}

/** Leads sitting in the protected `won` terminal stage and not already converted. */
function useWonLeads() {
  return useQuery({
    queryKey: ['won_leads'],
    queryFn: async (): Promise<WonLead[]> => {
      if (isDemoMode) {
        return [
          { id: 2, business_name: 'Дента Център Лазур', website_url: 'https://dentallazur.com' },
          {
            id: 6,
            business_name: 'Д-р Димитров - Естетична Стоматология',
            website_url: 'https://dr-dimitrov.bg',
          },
        ];
      }
      const { data: stages, error: stagesError } = await supabase
        .from('sales_stages')
        .select('id')
        .eq('terminal_kind', 'won');
      if (stagesError) throw new Error(stagesError.message);
      const wonStageIds = (stages ?? []).map((row) => row.id as number);
      // No won stage means nobody can be converted — not "everybody can". The guard used to
      // fall open here and offer every lead.
      if (wonStageIds.length === 0) return [];

      const { data: existing, error: existingError } = await supabase
        .from('clients')
        .select('lead_id');
      if (existingError) throw new Error(existingError.message);
      const converted = new Set((existing ?? []).map((row) => row.lead_id as number));

      let query = supabase
        .from('leads')
        .select('id, business_name, website_url')
        .is('parent_lead_id', null)
        .order('business_name');

      query = query.in('sales_stage_id', wonStageIds);

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as WonLead[]).filter((lead) => !converted.has(lead.id));
    },
  });
}

export function ConvertDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (clientId: number) => void;
}) {
  const { data: leads = [], isLoading } = useWonLeads();
  const convert = useConvertLead();

  const [leadId, setLeadId] = useState('');
  const [packageKey, setPackageKey] = useState('');
  const [oneTime, setOneTime] = useState('');
  const [monthly, setMonthly] = useState('');
  const [billingDay, setBillingDay] = useState('1');
  const [goLive, setGoLive] = useState('');
  const [siteUrl, setSiteUrl] = useState('');

  const chosen = leads.find((lead) => String(lead.id) === leadId);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label="Нов клиент"
      onClick={onClose}
    >
      <form
        className="modal__box"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          if (leadId === '') return;
          convert.mutate(
            {
              leadId: Number(leadId),
              packageKey,
              oneTimePriceEur: oneTime === '' ? null : Number(oneTime),
              monthlyFeeEur: monthly === '' ? null : Number(monthly),
              billingDay: Number(billingDay),
              goLiveDate: goLive === '' ? null : goLive,
              siteUrl: siteUrl === '' ? (chosen?.website_url ?? null) : siteUrl,
            },
            { onSuccess: onCreated },
          );
        }}
      >
        <h2 className="modal__title">Нов клиент</h2>

        {isLoading ? <p className="cl__msg">Зареждане…</p> : null}
        {!isLoading && leads.length === 0 ? (
          <p className="cl__msg">
            Няма лийдове в етап „Спечелена“. Преместете сделката там първо — клиентът се създава от
            спечелен лийд, не от произволен запис.
          </p>
        ) : null}

        <label>
          Лийд
          <select value={leadId} onChange={(event) => setLeadId(event.target.value)} required>
            <option value="">избери…</option>
            {leads.map((lead) => (
              <option key={lead.id} value={lead.id}>
                {lead.business_name}
              </option>
            ))}
          </select>
        </label>

        <label>
          Пакет
          <input
            type="text"
            value={packageKey}
            onChange={(event) => setPackageKey(event.target.value)}
            placeholder="напр. starter"
          />
        </label>

        <div className="modal__row">
          <label>
            Еднократно (EUR)
            <input
              type="number"
              min="0"
              step="1"
              value={oneTime}
              onChange={(event) => setOneTime(event.target.value)}
            />
          </label>
          <label>
            Месечно (EUR)
            <input
              type="number"
              min="0"
              step="1"
              value={monthly}
              onChange={(event) => setMonthly(event.target.value)}
            />
          </label>
        </div>

        <div className="modal__row">
          <label>
            Ден на фактуриране
            <input
              type="number"
              min="1"
              max="28"
              value={billingDay}
              onChange={(event) => setBillingDay(event.target.value)}
            />
          </label>
          <label>
            Пускане в експлоатация
            <input type="date" value={goLive} onChange={(event) => setGoLive(event.target.value)} />
          </label>
        </div>

        <label>
          Сайт
          <input
            type="url"
            value={siteUrl}
            onChange={(event) => setSiteUrl(event.target.value)}
            placeholder={chosen?.website_url ?? 'https://…'}
          />
        </label>

        <p className="cl__note">
          Еднократната сума се начислява веднага. Месечната такса се начислява от нощната задача на
          посочения ден — повторно пускане не удвоява начисленията.
        </p>

        {convert.isError ? <p className="cl__err">{(convert.error as Error).message}</p> : null}

        <div className="modal__actions">
          <button type="button" onClick={onClose}>
            Отказ
          </button>
          <button type="submit" disabled={leadId === '' || convert.isPending}>
            Създай клиент
          </button>
        </div>
      </form>
    </div>
  );
}
